import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth, tokenStore, type User } from '@/lib/api';
import { ensureNotifPermission } from '@/lib/notifications';
import { setupWebPush } from '@/lib/web-push';

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: (accessToken: string, refreshToken: string, user: User) => void;
  signOut: () => Promise<void>;
  updateUser: (patch: Partial<User>) => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => tokenStore.getUser());
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    auth
      .me()
      .then(setUser)
      .catch(() => {
        // session invalidated server-side — clear and bounce
        tokenStore.clear();
        setUser(null);
      })
      .finally(() => setLoading(false));
    // Re-arm web push on every page load — service worker may need a
    // fresh subscription after browser cleanups, and a no-op if already
    // subscribed.
    void setupWebPush();
  }, []);

  // FR-SEC-015 / FR-WEB-011 — cross-tab logout sync.
  //
  // Without this, signing out in tab A leaves tab B holding a live
  // access token for as long as the user has the tab open — because
  // tokenStore.clear() runs in tab A's memory, but tab B's React state
  // never re-reads localStorage. The refresh token IS cleared globally
  // (localStorage is shared), so tab B's access token silently expires
  // within an hour and the next API call 401s — but until then, the
  // stale tab can still read data and initiate WebAuthn ceremonies
  // against sensitive endpoints. Real security gap on shared machines
  // (ops team member signs out at end of shift; next shift opens the
  // same browser and finds tab B still logged in).
  //
  // The `storage` event fires in OTHER tabs when localStorage is
  // mutated in this origin — perfect fit. Read the canonical state
  // from tokenStore rather than trusting the event payload (avoids
  // partial-update races when access/refresh/user are written
  // back-to-back). Symmetric: a fresh sign-in in tab A also
  // propagates via the same event so tab B picks up the new user.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      // Only react to our own token keys — never to unrelated writes
      // (analytics, feature-flags, per-page localStorage) that would
      // trigger noisy re-mounts.
      if (e.key !== 'otpshield.access' && e.key !== 'otpshield.user') return;
      const currentUser = tokenStore.getUser();
      const currentAccess = tokenStore.getAccess();
      if (!currentAccess || !currentUser) {
        // Sign-out happened somewhere. Drop React state so the router
        // re-runs its auth check and bounces to /login.
        setUser(null);
      } else if (currentUser.id !== user?.id) {
        // Different user signed in on the same machine (shared kiosk,
        // colleague reused the tab). Adopt the new profile — no full
        // reload needed.
        setUser(currentUser);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user?.id]);

  const signIn = (accessToken: string, refreshToken: string, u: User) => {
    tokenStore.set(accessToken, refreshToken, u);
    setUser(u);
    // Ask for browser notification permission once after first sign-in so
    // OTP / approval / parking-lot events buzz the OS even when the tab is
    // minimized or in the background. The local foreground notifier is
    // SSE-driven; web push (next call) handles the backgrounded case.
    void ensureNotifPermission();
    void setupWebPush();
  };

  const signOut = async () => {
    await auth.logout();
    setUser(null);
  };

  // Patch the cached user in place — used when the user changes their
  // password and the must-change flag clears, without a full re-login.
  const updateUser = (patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      const access = tokenStore.getAccess();
      const refresh = tokenStore.getRefresh();
      if (access && refresh) tokenStore.set(access, refresh, next);
      return next;
    });
  };

  return (
    <AuthCtx.Provider value={{ user, loading, signIn, signOut, updateUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
